import React, { useMemo, useContext } from 'react';
import { Handle, Position } from 'reactflow';
import { Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DEFAULT_CANVAS_MODE } from '../../../../../../../utils/CanvasConstants';
import '../ComponentNode.css';
import PropTypes from 'prop-types';
import { ComponentNodeBodyView } from './ComponentNodeBodyView';
import { LoopHandles } from './LoopHandles';
import { CanvasContext } from '../../../../WorkflowCanvas';
import useProjectStore from '../../../../../../ProjectCanvas/useProjectStore';
import AddNewComponentButton from './AddNewComponentButton';
import AddConditionalPathButton from './AddConditionalPathButton';

const PathHandle = styled(Handle)(({ theme }) => ({
  backgroundColor: theme.palette.text.primary,
}));

const BRANCH_PATH_TYPE = 'branch_paths';
const SOURCE_TYPE = 'source';
const CONDITIONAL_WORKFLOW_PATH = 'conditional_workflow_path';

const ComponentNodeView = ({
  classes,
  data,
  selected,
  isSelectedComponent,
  isSelectedForceStart = false,
  mode,
  saving,
  addLinkedConditionalPath,
  nodeId,
  currentXPosition,
  currentYPosition,
  isHovered = false,
  hasLoop = false,
}) => {
  const { hasInput, hasOutput, fromWorkflowPath, type, isTemp, color, label, baseColor } = data;
  const isDraftCanvas = mode === DEFAULT_CANVAS_MODE;
  const { pendingConnectionSource: pendingConnectionSourceContext } = useContext(CanvasContext);
  const setPendingConnectionSource =
    Array.isArray(pendingConnectionSourceContext) && pendingConnectionSourceContext.length >= 2
      ? pendingConnectionSourceContext[1]
      : {};

  const svgSelectedClass = useMemo(
    () => (selected || isSelectedComponent || isSelectedForceStart ? classes.selectedIcon : ''),
    [selected, isSelectedComponent, isSelectedForceStart, classes.selectedIcon]
  );

  const renderInput = () => {
    if (!hasInput) return <div className={classes.filler} />;

    const handleProps = {
      className: classes.port,
      type: 'target',
      position: fromWorkflowPath ? 'left' : Position.Left,
      isConnectable: fromWorkflowPath ? false : !isTemp,
    };

    return fromWorkflowPath ? <PathHandle {...handleProps} /> : <Handle {...handleProps} />;
  };

  const renderOutput = () => {
    if (!hasOutput) return <div className={classes.filler} />;

    switch (type) {
      case BRANCH_PATH_TYPE:
        return (
          <>
            <PathHandle
              className={classes.port}
              position={Position.Right}
              type={SOURCE_TYPE}
              isConnectable={!isTemp}
            />
            {isDraftCanvas && (
              <AddConditionalPathButton
                disabled={saving}
                onClick={addLinkedConditionalPath}
                classes={classes}
                data={data}
              />
            )}
          </>
        );
      case CONDITIONAL_WORKFLOW_PATH:
        return (
          <PathHandle
            className={classes.port}
            position={Position.Right}
            type={SOURCE_TYPE}
            isConnectable={false}
          />
        );
      default:
        const handleAddComponentOnClick = event => {
          event.stopPropagation();
          if (setPendingConnectionSource) {
            setPendingConnectionSource({
              id: nodeId,
              position: { x: currentXPosition, y: currentYPosition },
              baseColor: data.baseColor,
              displayName: data.displayName,
            });
          } else {
            console.error('[ComponentNodeView] setPendingConnectionSource not found in context');
          }

          useProjectStore.getState().openComponentTray();
        };
        return (
          <>
            <Handle
              className={classes.port}
              position={Position.Right}
              type={SOURCE_TYPE}
              isConnectable={!isTemp}
            />
            {isDraftCanvas && (
              <AddNewComponentButton
                disabled={saving}
                isHovered={isHovered}
                onClick={handleAddComponentOnClick}
                classes={classes}
              />
            )}
          </>
        );
    }
  };

  const renderBody = () => {
    if (fromWorkflowPath) {
      return (
        <Button
          size="small"
          style={{
            backgroundColor: color.bg,
            border: `1px solid ${color.border}`,
          }}
          variant="outlined"
          className="draftVersionButton"
        >
          <span style={{ paddingLeft: '5px' }}>{label}</span>
        </Button>
      );
    }
    return (
      <ComponentNodeBodyView
        baseColor={baseColor}
        selectedClass={svgSelectedClass}
        data={data}
        classes={classes}
        hasLoop={hasLoop}
      />
    );
  };

  return (
    <div className={classes.nodeView} id="node-view">
      {renderInput()}
      {renderBody()}
      {renderOutput()}
      <LoopHandles />
    </div>
  );
};

ComponentNodeView.propTypes = {
  classes: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  isSelectedComponent: PropTypes.bool,
  isSelectedForceStart: PropTypes.bool,
  mode: PropTypes.string.isRequired,
  saving: PropTypes.bool,
  addLinkedConditionalPath: PropTypes.func.isRequired,
  nodeId: PropTypes.string.isRequired,
  currentXPosition: PropTypes.number,
  currentYPosition: PropTypes.number,
  isHovered: PropTypes.bool,
  hasLoop: PropTypes.bool,
};

export default ComponentNodeView;
